import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  formatDraftValue,
  harderSign,
  isEffortAxis,
  type DraftLineOptions,
  type IntensityKind,
  type PresetId,
  type ProgressionRule,
  type Recognition,
  type RuleName,
  type Sequence,
} from "@heraion/shared";
import { amrapStopShort } from "../amrapStopWords";

// The words of the builder (#336 slice 5, brief §5.4b): the name of a
// recognized rule, the presets, their fields, and the rule as a sentence
// («+1 per round up to RPE 9, last set AMRAP in the test round, deload
// RPE −2»). The numbers go through the lines' own formatter.

const signed = (n: number): string => (n > 0 ? `+${n}` : n < 0 ? `−${-n}` : "0");

export function useRuleWords(o: DraftLineOptions) {
  const { t } = useTranslation();
  return useMemo(() => {
    const value = (axis: IntensityKind, n: number) =>
      axis === "none" ? String(n) : formatDraftValue(axis, n, o);
    /** A step on `axis`: «+1», «+2.5 kg», «+5%». */
    const step = (axis: IntensityKind, d: number) =>
      axis === "absolute"
        ? `${d < 0 ? "−" : "+"}${formatDraftValue("absolute", Math.abs(d), o)}`
        : axis === "percent-of-max"
          ? `${signed(d)}%`
          : isEffortAxis(axis)
            ? `${axis.toUpperCase()} ${signed(d)}`
            : signed(d);

    const names: Record<RuleName, string> = {
      constant: t("editorV3.rule.name.constant", { defaultValue: "Constant" }),
      effortRamp: t("editorV3.rule.name.effortRamp", { defaultValue: "Effort ramp" }),
      tetrad: t("editorV3.rule.name.tetrad", { defaultValue: "Tetrad, test and deload" }),
      perSet: t("editorV3.rule.name.perSet", { defaultValue: "Per set" }),
      linear: t("editorV3.rule.name.linear", { defaultValue: "Linear" }),
      repsThenLoad: t("editorV3.rule.name.repsThenLoad", { defaultValue: "Reps, then load" }),
      triple: t("editorV3.rule.name.triple", { defaultValue: "Triple progression" }),
      timed: t("editorV3.rule.name.timed", { defaultValue: "Timed" }),
      history: t("editorV3.rule.name.history", { defaultValue: "Last time" }),
      waves: t("editorV3.rule.name.waves", { defaultValue: "Waves" }),
      blocks: t("editorV3.rule.name.blocks", { defaultValue: "In blocks" }),
      pyramid: t("editorV3.rule.name.pyramid", { defaultValue: "Pyramid" }),
      repsUp: t("editorV3.rule.name.repsUp", { defaultValue: "Rising reps" }),
      topBackoff: t("editorV3.rule.name.topBackoff", { defaultValue: "Top and back-off" }),
    };
    const presets: Record<PresetId, string> = {
      constant: names.constant,
      effortRamp: names.effortRamp,
      linearKg: t("editorV3.rule.preset.linearKg", { defaultValue: "Linear in kg" }),
      repsThenLoad: names.repsThenLoad,
      timed: names.timed,
      history: names.history,
      waves: names.waves,
      triple: names.triple,
      pyramid: names.pyramid,
      repsUp: names.repsUp,
      topBackoff: names.topBackoff,
    };
    const manual = t("editorV3.rule.manual", { defaultValue: "Values by hand" });

    const seqPiece = (axis: IntensityKind, s: Sequence | null, unit: (d: number) => string): string | null => {
      if (!s || s.kind === "constant") return null;
      if (s.kind === "linear") {
        const per = t("editorV3.rule.perRound", { step: unit(s.step), defaultValue: "{{step}} per round" });
        return s.cap === undefined
          ? per
          : t("editorV3.rule.upTo", { what: per, cap: value(axis, s.cap), defaultValue: "{{what}} up to {{cap}}" });
      }
      if (s.kind === "ramp") {
        return t("editorV3.rule.ramp", {
          from: value(axis, s.from),
          to: value(axis, s.to),
          count: s.rounds,
          defaultValue: "from {{from}} to {{to}} over {{count}} rounds",
        });
      }
      if (s.kind === "cycle") {
        return t("editorV3.rule.cycle", { values: s.values.join(" · "), defaultValue: "in turn {{values}}" });
      }
      return t("editorV3.rule.blocks", { count: s.blocks.length, defaultValue: "{{count}} blocks, each starting again" });
    };

    /** The rule in one line. */
    const sentence = (r: ProgressionRule): string => {
      const parts: (string | null)[] = [];
      const th = r.threshold;
      if (th) {
        parts.push(
          t("editorV3.rule.threshold", {
            min: th.repsMin,
            max: th.repsMax,
            step: th.repsStep,
            defaultValue: "reps {{min}} to {{max}} by {{step}}",
          }),
        );
        if (th.setsMax !== undefined) {
          parts.push(t("editorV3.rule.thenSets", { n: th.setsMax, defaultValue: "then sets up to {{n}}" }));
        }
        parts.push(t("editorV3.rule.thenLoad", { step: step(r.axis, th.loadStep), defaultValue: "then {{step}}" }));
      } else {
        parts.push(seqPiece(r.axis, r.value, (d) => step(r.axis, d)));
        // The steps inside the round: the pyramid's, or the back-off sets'.
        const pr = r.perSet?.reps?.[1];
        const pv = r.perSet?.value?.[1];
        const even = (xs?: number[]) => !xs || xs.every((x, i) => Math.abs(x - i * (xs[1] ?? 0)) < 1e-6);
        const backoff = !!pv && !pr && pv * harderSign(r.axis) < 0 && !!r.perSet?.value?.slice(1).every((x) => x === pv);
        if (backoff && isEffortAxis(r.axis)) {
          parts.push(t("editorV3.rule.backoff", { step: step(r.axis, pv), defaultValue: "back-off sets {{step}}" }));
        } else if ((pr || pv) && even(r.perSet?.reps) && even(r.perSet?.value)) {
          const steps = [
            pr ? t("editorV3.rule.repsStep", { step: signed(pr), defaultValue: "{{step}} reps" }) : null,
            pv ? step(r.axis, pv) : null,
          ].filter(Boolean);
          parts.push(t("editorV3.rule.eachSet", { steps: steps.join(", "), defaultValue: "each set {{steps}}" }));
        }
        parts.push(
          seqPiece("none", r.reps, (d) =>
            r.work ? `${signed(d)} ${o.labels.seconds}` : t("editorV3.rule.repsStep", { step: signed(d), defaultValue: "{{step}} reps" }),
          ),
        );
      }
      const te = r.test;
      if (te?.amrapLast) {
        const base = t("editorV3.rule.amrapInTest", { defaultValue: "last set AMRAP in the test round" });
        // #355: the stop rule, when the rule has one.
        parts.push(te.amrapStop ? `${base} (${amrapStopShort(t, te.amrapStop)})` : base);
      }
      if (te?.delta) {
        const d = step(r.axis, te.delta);
        parts.push(
          te.on === "last"
            ? t("editorV3.rule.testLast", { step: d, defaultValue: "test round {{step}} on the last set" })
            : te.on === "top"
              ? t("editorV3.rule.testTop", { step: d, defaultValue: "test round {{step}} on the top sets" })
              : t("editorV3.rule.test", { step: d, defaultValue: "test round {{step}}" }),
        );
      }
      const de = r.deload;
      if (de?.factor !== undefined && de.factor !== 1) {
        parts.push(t("editorV3.rule.deloadPct", { pct: Math.round(de.factor * 100), defaultValue: "deload at {{pct}}%" }));
      }
      if (de?.delta) parts.push(t("editorV3.rule.deload", { step: step(r.axis, de.delta), defaultValue: "deload {{step}}" }));
      // #356: a row with no value deloads at an effort, on the scale its cells use.
      if (de?.effort) {
        parts.push(t("editorV3.rule.deloadEffort", { value: value(de.effort.axis, de.effort.value), defaultValue: "deload at {{value}}" }));
      }
      if (de?.setsDelta) {
        parts.push(t("editorV3.rule.deloadSets", { count: -de.setsDelta, defaultValue: "{{count}} set fewer in deload" }));
      }
      const said = parts.filter((p): p is string => !!p);
      return said.length > 0 ? said.join(", ") : t("editorV3.rule.same", { defaultValue: "the same every round" });
    };

    return {
      names,
      presets,
      manual,
      sentence,
      step,
      /** «Rule · Effort ramp», or «Values by hand». */
      title: (rec: Recognition) =>
        rec.kind === "rule"
          ? t("editorV3.rule.title", { name: names[rec.name], defaultValue: "Rule · {{name}}" })
          : manual,
    };
  }, [t, o]);
}

export type RuleWords = ReturnType<typeof useRuleWords>;
